import { useEffect } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import type { FieldValues, SubmitHandler } from 'react-hook-form';

import Heading from 'components/partials/heading/heading';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Loader from 'components/partials/loader/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import ClientForm from 'containers/admin/clients/client/forms/client-form';

import { useCreateOrUpdateClient } from 'hooks/api/use-clients';

import { transformProfileFormData } from 'helpers/forms';
import type { ProfileFormValues } from 'helpers/forms';

interface ClientProfileProps {
  pageName?: string;
  isFormDisabled?: boolean;
  setIsFormDisabled?: (value: boolean) => void;
  showClientDeleteBtn?: boolean;
  defaultValues?: Partial<ProfileFormValues> | Record<string, unknown>;
}

const DEFAULT_VALUES: Record<string, unknown> = {
  name: '',
  authorizedDomains: [],
  externalId: '',
  state: '',
  expireDate: '01/01/2100',
};

const ClientProfile = ({
  pageName = 'Profile information',
  isFormDisabled = false,
  setIsFormDisabled,
  showClientDeleteBtn,
  defaultValues = DEFAULT_VALUES,
}: ClientProfileProps): React.JSX.Element => {
  const navigate = useNavigate();
  const createOrUpdateClient = useCreateOrUpdateClient();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    clearErrors,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: defaultValues as FieldValues,
  });

  useEffect(() => {
    if (defaultValues.id) {
      reset(defaultValues as FieldValues);
    }
  }, [defaultValues.id, reset]);

  const loading = createOrUpdateClient.isPending;

  const onSubmit: SubmitHandler<FieldValues> = (formData) => {
    const dataToSend = transformProfileFormData(
      formData as ProfileFormValues
    ) as { id?: string | number; name?: string } & Record<string, unknown>;

    createOrUpdateClient.mutate(dataToSend, {
      onSuccess: () => {
        navigate('/admin/clients');
        reset();
      },
    });
  };

  const handleCancel = (): void => {
    reset();

    if (setIsFormDisabled) {
      setIsFormDisabled(true);
    } else {
      navigate('/admin/clients');
    }
  };

  return (
    <ResponsiveWrapper>
      <Loader {...{ loading }} />
      {!showClientDeleteBtn && (
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Clients', href: '/admin/clients' },
            { label: 'New client', isActive: true },
          ]}
        />
      )}
      <div className="flex flex-col w-full mt-8">
        <div className="flex items-center justify-between">
          <Heading
            variant={!!showClientDeleteBtn ? 'h2' : 'h1'}
            customClass="mb-8"
          >
            {pageName}
          </Heading>

          {!!showClientDeleteBtn && setIsFormDisabled && (
            <ActionMenu
              onEditClick={() => setIsFormDisabled(false)}
              isActionMenuDisabled={!isFormDisabled}
            />
          )}
        </div>

        <ClientForm
          {...{
            isFormDisabled,
            onCancel: handleCancel,
            control,
            onSubmit,
            handleSubmit,
            errors,
            clearErrors,
          }}
        />
      </div>
    </ResponsiveWrapper>
  );
};

export default ClientProfile;
